import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { WaveSine, Envelope, LockKey } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function errText(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  const handleGoogle = () => {
    window.location.href = '/api/auth/google/start?next=%2Fdashboard';
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.session_token) localStorage.setItem('session_token', data.session_token);
      setUser(data.user);
      nav('/dashboard');
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <WaveSine size={26} weight="duotone" className="text-[#ccff00]" />
          <span className="font-display text-xl font-bold tracking-tight">TUNEAMPLIFIER</span>
        </Link>
        <div className="border border-zinc-800 rounded-2xl bg-zinc-900/50 p-8">
          <h1 className="font-display text-3xl font-bold">Welcome back.</h1>
          <p className="text-sm text-zinc-400 mt-1 mb-6">Log in to keep pushing your sound.</p>
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="login-email-input" type="email" required placeholder="Email" value={email}
                onChange={e => setEmail(e.target.value)} className="pl-9 bg-zinc-950 border-zinc-800" />
            </div>
            <div className="relative">
              <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="login-password-input" type="password" required placeholder="Password" value={password}
                onChange={e => setPassword(e.target.value)} className="pl-9 bg-zinc-950 border-zinc-800" />
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs font-mono text-zinc-500 hover:text-[#ccff00]">Forgot password?</Link>
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full btn-neon rounded-full font-display font-semibold h-11">
              {loading ? 'Logging in…' : 'Log in'}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-zinc-800" /><span className="text-xs font-mono text-zinc-600">OR</span><div className="flex-1 h-px bg-zinc-800" />
          </div>
          <button data-testid="login-google-btn" onClick={handleGoogle}
            className="w-full rounded-full border border-zinc-700 hover:border-zinc-500 py-2.5 text-sm font-mono transition-[border-color]">
            Continue with Google
          </button>
          <p className="text-center text-sm text-zinc-500 mt-6">
            No account? <Link to="/register" data-testid="goto-register-link" className="text-[#ccff00] hover:underline">Sign up free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
